import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import {
   asNumberOrNull,
   asString,
   formatCurrency
} from '../utils/report-formatting.utils';
import { getReportArray } from '../utils/report-json.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

const LAYOUT_SUMMARY = 'summary';
const LAYOUT_GROUPED_BY_CASHIER = 'grouped-by-cashier';
const LAYOUT_PAYMENT_SUMMARY = 'payment-summary';

function getCashiers(reportJson: unknown): any[] {
   return getReportArray(reportJson, 'CashierSummary');
}

function getCashierGroupKey(cashier: any): string {
   return asString(cashier?.CashierId) || getRealTyallsRowId(cashier);
}

function getCashierRowId(cashier: any): string {
   return getRealTyallsRowId(cashier) || `cashier:${asString(cashier?.CashierId)}`;
}

function getTransactionRowId(cashier: any, summary: any): string {
   return (
      getRealTyallsRowId(summary) ||
      `${getCashierRowId(cashier)}:transaction:${asString(
         summary?.TransactionCode
      )}:${asString(summary?.TransactionCodeTotal)}`
   );
}

function getTransactionDescription(summary: any): string {
   const descriptionCandidates = [
      summary?.Description,
      summary?.description,
      summary?.TransactionDescription,
      summary?.transactionDescription,
      summary?.TransactionCodeDescription,
      summary?.transactionCodeDescription
   ];

   for (const candidate of descriptionCandidates) {
      const description = asString(candidate).trim();

      if (description) {
         return description;
      }
   }

   return '';
}

function getTransactionNet(summary: any): number | null {
   return (
      asNumberOrNull(summary?.Net) ??
      asNumberOrNull(summary?.TransactionCodeTotal) ??
      asNumberOrNull(summary?.LocalAmount)
   );
}

function joinFolioReferences(value: unknown): string {
   if (!Array.isArray(value)) {
      return '';
   }

   return value.map(asString).filter(Boolean).join('   •   ');
}

function groupCashiers(cashiers: any[]): any[] {
   const groupedCashiers = new Map<string, any>();

   for (const cashier of cashiers) {
      const key = getCashierGroupKey(cashier);

      if (!key) {
         continue;
      }

      const existingCashier = groupedCashiers.get(key);

      if (!existingCashier) {
         groupedCashiers.set(key, {
            ...cashier,
            TransactionSummaries: Array.isArray(cashier?.TransactionSummaries)
               ? [...cashier.TransactionSummaries]
               : []
         });

         continue;
      }

      const existingSummaries = Array.isArray(existingCashier.TransactionSummaries)
         ? existingCashier.TransactionSummaries
         : [];

      const newSummaries = Array.isArray(cashier?.TransactionSummaries)
         ? cashier.TransactionSummaries
         : [];

      existingCashier.TransactionSummaries = [...existingSummaries, ...newSummaries];

      const existingTotal = asNumberOrNull(existingCashier.CashierTotal) ?? 0;

      const nextTotal = asNumberOrNull(cashier?.CashierTotal) ?? 0;

      existingCashier.CashierTotal = existingTotal + nextTotal;
   }

   return Array.from(groupedCashiers.values()).sort((a, b) => {
      const aId = Number(a?.CashierId);
      const bId = Number(b?.CashierId);

      if (Number.isFinite(aId) && Number.isFinite(bId)) {
         return aId - bId;
      }

      return asString(a?.CashierId).localeCompare(asString(b?.CashierId));
   });
}

function sortTransactionSummaries(summaries: any[]): any[] {
   return [...summaries].sort((a, b) => {
      const aCode = Number(a?.TransactionCode);
      const bCode = Number(b?.TransactionCode);

      if (Number.isFinite(aCode) && Number.isFinite(bCode)) {
         return aCode - bCode;
      }

      return asString(a?.TransactionCode).localeCompare(asString(b?.TransactionCode));
   });
}

function flattenGroupedByCashier(reportJson: unknown): ReportRowVm[] {
   const cashiers = groupCashiers(getCashiers(reportJson));
   const rows: ReportRowVm[] = [];

   for (const cashier of cashiers) {
      const cashierNumber = asString(cashier?.CashierId);
      const cashierName = asString(cashier?.CashierName ?? cashier?.UserName);
      const cashierTotalRaw = asNumberOrNull(cashier?.CashierTotal);

      const cashierDisplay = cashierName
         ? `CASHIER: ${cashierNumber}  — ${cashierName}`
         : `CASHIER: ${cashierNumber}`;

      rows.push({
         rowId: `cashier-summary:${LAYOUT_GROUPED_BY_CASHIER}:cashier:${getCashierRowId(
            cashier
         )}:header`,
         rowClass: 'report-group-header-row',
         isCheckable: false,
         cells: {
            rowType: 'groupHeader',
            leadingColumnSpan: 3,
            transactionCode: cashierDisplay,
            paymentType: '',
            folioReferences: '',
            debit: '',
            debitRaw: null,
            credit: '',
            creditRaw: null,
            netTotal: formatCurrency(cashierTotalRaw),
            netTotalRaw: cashierTotalRaw
         }
      });

      const transactionSummaries = Array.isArray(cashier?.TransactionSummaries)
         ? sortTransactionSummaries(cashier.TransactionSummaries)
         : [];

      for (const summary of transactionSummaries) {
         const debitRaw = asNumberOrNull(summary?.Debit) ?? 0;
         const creditRaw = asNumberOrNull(summary?.Credit) ?? 0;
         const netTotalRaw = getTransactionNet(summary);

         rows.push({
            rowId: getTransactionRowId(cashier, summary),
            cells: {
               rowType: 'transaction',
               transactionCode: asString(summary?.TransactionCode),
               transactionCodeRaw: asString(summary?.TransactionCode),
               paymentType: getTransactionDescription(summary),
               folioReferences: joinFolioReferences(summary?.FolioReferences),
               debit: formatCurrency(debitRaw),
               debitRaw,
               credit: formatCurrency(creditRaw),
               creditRaw,
               netTotal: formatCurrency(netTotalRaw),
               netTotalRaw
            }
         });
      }
   }

   return rows;
}

function flattenCashSummary(reportJson: unknown): ReportRowVm[] {
   const cashiers = groupCashiers(getCashiers(reportJson));

   const summaryByType = new Map<
      string,
      {
         label: string;
         totalNet: number;
         sourceRowIds: string[];
      }
   >([
      [
         'cash',
         {
            label: 'Cash',
            totalNet: 0,
            sourceRowIds: []
         }
      ],
      [
         'cash-drop',
         {
            label: 'Cash Drop',
            totalNet: 0,
            sourceRowIds: []
         }
      ]
   ]);

   for (const cashier of cashiers) {
      const transactionSummaries = Array.isArray(cashier?.TransactionSummaries)
         ? cashier.TransactionSummaries
         : [];

      for (const summary of transactionSummaries) {
         const description = getTransactionDescription(summary).trim().toLowerCase();

         const summaryType =
            description === 'cash'
               ? 'cash'
               : description === 'cash drop'
                 ? 'cash-drop'
                 : null;

         if (!summaryType) {
            continue;
         }

         const summaryRow = summaryByType.get(summaryType);

         if (!summaryRow) {
            continue;
         }

         summaryRow.totalNet += getTransactionNet(summary) ?? 0;

         const sourceRowId = getRealTyallsRowId(summary);

         if (sourceRowId) {
            summaryRow.sourceRowIds.push(sourceRowId);
         }
      }
   }

   return Array.from(summaryByType.entries()).map(([key, summary]) => ({
      rowId: `cashier-summary:${LAYOUT_SUMMARY}:${key}`,
      sourceRowIds: Array.from(new Set(summary.sourceRowIds)),
      cells: {
         rowType: 'cashSummary',
         paymentType: summary.label,
         totalNet: formatCurrency(summary.totalNet),
         totalNetRaw: summary.totalNet
      }
   }));
}

function flattenPaymentSummary(reportJson: unknown): ReportRowVm[] {
   const cashiers = groupCashiers(getCashiers(reportJson));

   const summaryByCode = new Map<
      string,
      {
         code: string;
         paymentType: string;
         totalNet: number;
         sourceRowIds: string[];
      }
   >();

   for (const cashier of cashiers) {
      const transactionSummaries = Array.isArray(cashier?.TransactionSummaries)
         ? cashier.TransactionSummaries
         : [];

      for (const summary of transactionSummaries) {
         const code = asString(summary?.TransactionCode);

         if (!code) {
            continue;
         }

         const existingSummary = summaryByCode.get(code) ?? {
            code,
            paymentType: getTransactionDescription(summary),
            totalNet: 0,
            sourceRowIds: []
         };

         if (!existingSummary.paymentType) {
            existingSummary.paymentType = getTransactionDescription(summary);
         }

         existingSummary.totalNet += getTransactionNet(summary) ?? 0;

         const sourceRowId = getRealTyallsRowId(summary);

         if (sourceRowId) {
            existingSummary.sourceRowIds.push(sourceRowId);
         }

         summaryByCode.set(code, existingSummary);
      }
   }

   return Array.from(summaryByCode.values())
      .sort((a, b) => {
         const aCode = Number(a.code);
         const bCode = Number(b.code);

         if (Number.isFinite(aCode) && Number.isFinite(bCode)) {
            return aCode - bCode;
         }

         return a.code.localeCompare(b.code);
      })
      .map((summary) => ({
         rowId: `cashier-summary:${LAYOUT_PAYMENT_SUMMARY}:transaction-code:${summary.code}`,
         sourceRowIds: Array.from(new Set(summary.sourceRowIds)),
         cells: {
            rowType: 'paymentSummary',
            code: summary.code,
            codeRaw: summary.code,
            paymentType: summary.paymentType,
            totalNet: formatCurrency(summary.totalNet),
            totalNetRaw: summary.totalNet
         }
      }));
}

export const CASHIER_SUMMARY_DEFINITION: ReportDefinition = {
   reportType: 'CashierSummary',
   version: 5,
   title: 'Cashier Summary',

   header: {
      title: 'Cashier Summary'
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      excludeRowTypes: ['groupHeader', 'groupTotal', 'cashierTotal']
   },

   annotations: false,

   columns: [
      {
         key: 'transactionCode',
         header: 'TRN CODE',
         width: '170px'
      },
      {
         key: 'paymentType',
         header: 'DESCRIPTION',
         width: '260px'
      },
      {
         key: 'folioReferences',
         header: 'FOLIO REFERENCES',
         width: '520px',
         maxChars: 120
      },
      {
         key: 'debit',
         header: 'DEBIT',
         width: '150px'
      },
      {
         key: 'credit',
         header: 'CREDIT',
         width: '150px'
      },
      {
         key: 'netTotal',
         header: 'NET TOTAL',
         width: '160px'
      }
   ],

   flattenRows: flattenGroupedByCashier,

   filter: {
      fields: [
         {
            key: 'transactionCode',
            label: 'Transaction Code',
            type: 'string'
         },
         {
            key: 'paymentType',
            label: 'Payment Type',
            type: 'string'
         },
         {
            key: 'folioReferences',
            label: 'Folio References',
            type: 'string'
         },
         {
            key: 'debit',
            label: 'Debit',
            type: 'number',
            getValue: (row) => row.cells['debitRaw']
         },
         {
            key: 'credit',
            label: 'Credit',
            type: 'number',
            getValue: (row) => row.cells['creditRaw']
         },
         {
            key: 'netTotal',
            label: 'Net Total',
            type: 'number',
            getValue: (row) => row.cells['netTotalRaw']
         }
      ]
   },

   layouts: [
      {
         id: LAYOUT_SUMMARY,
         label: 'Summary',
         sorting: false,
         annotations: {
            enabled: false,
            disabledMessage:
               'Row comments and row highlighting are not available for Summary because this layout is calculated from multiple rows. Please use the main report comments panel to add comments for this report.'
         },
         columns: [
            {
               key: 'paymentType',
               header: 'TYPE',
               width: '260px'
            },
            {
               key: 'totalNet',
               header: 'AMOUNT',
               width: '180px'
            }
         ],
         flattenRows: flattenCashSummary,
         filter: {
            fields: []
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['cashSummary']
         }
      },
      {
         id: LAYOUT_GROUPED_BY_CASHIER,
         label: 'Grouped by Cashier',
         sorting: false,
         annotations: {
            enabled: true,
            selectableRowTypes: ['transaction'],
            disabledMessage:
               'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
         },
         columns: [
            {
               key: 'transactionCode',
               header: 'TRN CODE',
               width: '170px'
            },
            {
               key: 'paymentType',
               header: 'DESCRIPTION',
               width: '260px'
            },
            {
               key: 'folioReferences',
               header: 'FOLIO REFERENCES',
               width: '520px',
               maxChars: 120
            },
            {
               key: 'debit',
               header: 'DEBIT',
               width: '150px'
            },
            {
               key: 'credit',
               header: 'CREDIT',
               width: '150px'
            },
            {
               key: 'netTotal',
               header: 'NET TOTAL',
               width: '160px'
            }
         ],
         flattenRows: flattenGroupedByCashier,
         filter: {
            fields: [
               {
                  key: 'transactionCode',
                  label: 'Transaction Code',
                  type: 'string'
               },
               {
                  key: 'paymentType',
                  label: 'Payment Type',
                  type: 'string'
               },
               {
                  key: 'folioReferences',
                  label: 'Folio References',
                  type: 'string'
               },
               {
                  key: 'debit',
                  label: 'Debit',
                  type: 'number',
                  getValue: (row) => row.cells['debitRaw']
               },
               {
                  key: 'credit',
                  label: 'Credit',
                  type: 'number',
                  getValue: (row) => row.cells['creditRaw']
               },
               {
                  key: 'netTotal',
                  label: 'Net Total',
                  type: 'number',
                  getValue: (row) => row.cells['netTotalRaw']
               }
            ]
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['transaction']
         }
      },
      {
         id: LAYOUT_PAYMENT_SUMMARY,
         label: 'Payment Summary',
         sorting: {
            enabled: true,
            persist: true
         },
         annotations: {
            enabled: false,
            disabledMessage:
               'Row comments and row highlighting are not available for Payment Summary because this layout is calculated from multiple rows. Please use the main report comments panel to add comments for this report.'
         },
         columns: [
            {
               key: 'code',
               header: 'CODE',
               width: '140px',
               sort: {
                  type: 'string',
                  getValue: (row) => row.cells['codeRaw']
               }
            },
            {
               key: 'paymentType',
               header: 'PAYMENT TYPE',
               width: '360px',
               sort: { type: 'string' }
            },
            {
               key: 'totalNet',
               header: 'TOTAL NET',
               width: '180px',
               sort: {
                  type: 'number',
                  getValue: (row) => row.cells['totalNetRaw'],
                  defaultDirection: 'desc'
               }
            }
         ],
         flattenRows: flattenPaymentSummary,
         filter: {
            fields: [
               {
                  key: 'code',
                  label: 'Code',
                  type: 'string'
               },
               {
                  key: 'paymentType',
                  label: 'Payment Type',
                  type: 'string'
               },
               {
                  key: 'totalNet',
                  label: 'Total Net',
                  type: 'number',
                  getValue: (row) => row.cells['totalNetRaw']
               }
            ]
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['paymentSummary']
         }
      }
   ]
};
